import { useMemo } from 'react';
import { Highlighter } from 'react-bootstrap-typeahead';

import { getEventsLOV, type EventSummary } from 'api/events/operations/getEventsLOV';

import { EditReference } from './EditReference';

interface Props {
  // The stored `zone_name|name` event path, or null for "no event".
  readonly value: string | null;
  readonly onChange: (value: string | null) => void;
  readonly label?: string;
  readonly mandatory?: boolean;
  readonly disabled?: boolean;
}

const getKey = (e: EventSummary) => `${e.zone_name}|${e.name}`;
const getLabel = (e: EventSummary): string => `${e.name} (${e.zone_name})`;

/**
 * Searchable dropdown for (process) events keyed on the `zone_name|name` path — the shape wait/raise-event links store,
 * distinct from the `event_id`-keyed {@link EditEventReference} (used where a numeric id is stored). Like
 * {@link EditQueueReference}, the stored value already carries both display parts, so the interim item built by splitting it
 * has a real label: the field reads correctly before the eager LOV even loads. Selection returns the composite path.
 */
export const EditEventPathReference = ({ value, onChange, label = 'Event', mandatory, disabled }: Props) => {
  const selected = useMemo<EventSummary | null>(() => {
    if (value === null || value === '') return null;
    const [zone_name = '', name = ''] = value.split('|');
    return { event_id: 0, name, zone_name };
  }, [value]);

  return (
    <EditReference<EventSummary>
      label={label}
      value={selected}
      onChange={(e) => onChange(e !== null ? getKey(e) : null)}
      source={getEventsLOV}
      getKey={getKey}
      getLabel={getLabel}
      mandatory={mandatory}
      disabled={disabled}
      renderMenuItemChildren={(e, query) => (
        <div className="d-flex justify-content-between gap-3">
          <span>
            <Highlighter search={query}>{e.name}</Highlighter>
          </span>
          <span className="text-body-secondary">{e.zone_name}</span>
        </div>
      )}
    />
  );
};
